import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { GoldButton } from "./shared";
import { ArrowRight } from "lucide-react";

/* Gold credit card that swipes through a reader, then "APPROVED" */
function GoldCard() {
  return (
    <div className="relative h-52 w-[340px] rounded-2xl p-6 shadow-[0_20px_60px_rgba(212,175,55,0.35)]"
      style={{ background: "linear-gradient(135deg,#f3e5ab 0%,#d4af37 40%,#aa7c11 100%)" }}>
      <div className="absolute inset-0 rounded-2xl opacity-30"
        style={{ background: "linear-gradient(115deg,transparent 40%,rgba(255,255,255,0.6) 50%,transparent 60%)" }} />
      <div className="flex items-start justify-between">
        <span className="font-display text-2xl font-bold tracking-[0.15em] text-[#3a2a05]">BGM</span>
        <span className="font-body text-[9px] font-semibold tracking-[0.2em] text-[#3a2a05]/70">FINANCIAL</span>
      </div>
      <div className="mt-5 h-9 w-12 rounded-md bg-[#3a2a05]/25" />
      <div className="mt-5 flex gap-3 font-display text-base tracking-[0.25em] text-[#3a2a05]">
        <span>••••</span><span>••••</span><span>••••</span><span>2026</span>
      </div>
      <div className="mt-3 flex items-end justify-between">
        <span className="font-body text-[10px] font-semibold tracking-[0.15em] text-[#3a2a05]/80">MEMBER · BUILD GROW MAINTAIN</span>
      </div>
    </div>
  );
}

export default function Intro({ onEnter }) {
  const [phase, setPhase] = useState("insert"); // insert -> swipe -> approved

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { setPhase("approved"); return; }
    const t1 = setTimeout(() => setPhase("swipe"), 1200);
    const t2 = setTimeout(() => setPhase("approved"), 2000);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const cardVariants = {
    insert: { x: -520, rotate: -8, opacity: 0 },
    swipeIn: { x: 0, rotate: 0, opacity: 1, transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1] } },
    swipeOut: { x: 620, rotate: 6, opacity: 0, transition: { duration: 0.55, ease: [0.76, 0, 0.24, 1] } },
  };

  return (
    <motion.section
      className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#050505] px-6 text-center"
      exit={{ opacity: 0, filter: "blur(6px)" }}
      transition={{ duration: 0.8 }}
      data-testid="intro-screen"
    >
      <div className="spotlight pointer-events-none absolute inset-0" />

      {/* card reader slot */}
      <div className="relative flex h-64 w-full max-w-xl items-center justify-center">
        <div className="absolute h-72 w-2 rounded-full bg-gradient-to-b from-transparent via-[#d4af37]/60 to-transparent" />
        <AnimatePresence>
          {phase !== "approved" && (
            <motion.div
              variants={cardVariants}
              initial="insert"
              animate={phase === "swipe" ? "swipeOut" : "swipeIn"}
              exit={{ opacity: 0 }}
              className="relative z-10"
            >
              <GoldCard />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {phase === "approved" && (
            <motion.div
              initial={{ scale: 0.4, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", stiffness: 220, damping: 16 }}
              className="absolute z-10 flex flex-col items-center"
            >
              <motion.div
                initial={{ scale: 0 }} animate={{ scale: 1 }}
                transition={{ delay: 0.1, type: "spring", stiffness: 260 }}
                className="mb-5 flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#d4af37]"
              >
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <motion.path d="M4 12l5 5L20 6" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ delay: 0.25, duration: 0.5 }} />
                </svg>
              </motion.div>
              <span className="monogram text-6xl font-bold tracking-[0.15em] sm:text-7xl">APPROVED</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {phase === "approved" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="relative z-10 mt-6 flex flex-col items-center"
          >
            <p className="overline mb-2">Welcome to</p>
            <h1 className="font-display text-2xl font-light tracking-tighter-x text-[#f5f5f5] sm:text-4xl">
              BGM FINANCIAL <span className="gold-text-gradient font-normal">SOLUTIONS</span>
            </h1>
            <p className="mt-2 font-editorial text-xl italic text-[#a3a3a3]">Build. Grow. Maintain.</p>
            <GoldButton testid="enter-experience-btn" onClick={onEnter} className="mt-8 px-9 py-4 text-base">
              Enter your financial future
            </GoldButton>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.p
        initial={{ opacity: 0 }} animate={{ opacity: phase === "approved" ? 0 : 0.7 }}
        className="absolute bottom-10 z-10 font-body text-[11px] tracking-[0.3em] text-[#666]"
      >
        {phase === "insert" ? "INITIALIZING YOUR ACCESS…" : phase === "swipe" ? "VERIFYING…" : ""}
      </motion.p>
    </motion.section>
  );
}
